import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { UploadCloud, FileText, Image as ImageIcon, X, Loader2, CheckCircle2, User } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/client-upload")({
  head: () => ({
    meta: [
      { title: "Client File Upload" },
      { name: "description", content: "Upload client documents and images securely." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/client-upload" }],
  }),
  component: ClientUploadPage,
});

const ACCEPT = "image/png,image/jpeg,image/jpg,application/pdf";
const MAX_MB = 25;
const SIGNED_URL_TTL = 60 * 60 * 24 * 365 * 10; // ~10 years

function sanitizeFolder(name: string) {
  return name.trim().replace(/[^\w\s\-.]/g, "_").replace(/\s+/g, "_");
}

function isAllowed(file: File) {
  return /^(image\/(png|jpe?g)|application\/pdf)$/i.test(file.type);
}

function ClientUploadPage() {
  const [clientName, setClientName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ name: string; url: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSelect = (f: File | null) => {
    if (!f) return setFile(null);
    if (!isAllowed(f)) return toast.error("Only PNG, JPG, or PDF files are allowed.");
    if (f.size > MAX_MB * 1024 * 1024) return toast.error(`File must be under ${MAX_MB}MB.`);
    setFile(f);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) onSelect(e.dataTransfer.files[0]);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) onSelect(e.target.files[0]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const canSubmit = clientName.trim().length > 0 && !!file && !submitting;

  async function handleSubmit() {
    if (!clientName.trim()) return toast.error("Please enter a client name.");
    if (!file) return toast.error("Please select a file.");
    setSubmitting(true);
    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user) {
        toast.error("Please sign in to upload files.");
        setSubmitting(false);
        return;
      }
      const userId = userData.user.id;
      const folder = sanitizeFolder(clientName);
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const path = `${userId}/${folder}/${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage
        .from("client-uploads")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;

      const { data: signed, error: sErr } = await supabase.storage
        .from("client-uploads")
        .createSignedUrl(path, SIGNED_URL_TTL);
      if (sErr) throw sErr;
      const fileUrl = signed?.signedUrl ?? path;

      const { error: dbErr } = await supabase
        .from("uploaded_files")
        .insert({ client_name: clientName.trim(), file_url: fileUrl, user_id: userId });
      if (dbErr) throw dbErr;

      setSuccess({ name: clientName.trim(), url: fileUrl });
      toast.success(`✅ Upload complete for ${clientName.trim()}!`);
      setClientName("");
      setFile(null);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SiteShell>
      <section className="container-page py-16 md:py-24">
        <div className="mx-auto max-w-2xl">
          <div className="text-center mb-10">
            <div className="mx-auto h-16 w-16 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow">
              <UploadCloud className="h-8 w-8" />
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl mt-5">
              Client File <span className="gradient-text">Upload</span>
            </h1>
            <p className="text-muted-foreground mt-3">
              Upload PNG, JPG, or PDF files. Organized automatically by client name.
            </p>
          </div>

          {success ? (
            <div className="glass-strong rounded-3xl p-8 text-center shadow-card">
              <div className="mx-auto h-16 w-16 rounded-full bg-success/15 text-success flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9" />
              </div>
              <h2 className="font-display font-bold text-2xl mt-5">Upload Successful 🎉</h2>
              <p className="mt-2 text-muted-foreground">
                File uploaded for <span className="font-semibold text-foreground">{success.name}</span>.
              </p>
              <div className="mt-6 flex gap-3 justify-center">
                <Button asChild variant="outline">
                  <a href={success.url} target="_blank" rel="noopener noreferrer">View File</a>
                </Button>
                <Button onClick={() => setSuccess(null)} className="gradient-primary text-primary-foreground">
                  Upload Another
                </Button>
              </div>
            </div>
          ) : (
            <div className="glass-strong rounded-3xl p-6 md:p-8 shadow-card space-y-6">
              <div>
                <Label htmlFor="client-name" className="flex items-center gap-2">
                  <User className="h-4 w-4" /> Client Name
                </Label>
                <Input
                  id="client-name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. Rahim Trading Co."
                  className="mt-2"
                />
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
                  dragOver ? "border-primary bg-primary/5 scale-[1.01]" : "border-border bg-background/40"
                }`}
              >
                {file ? (
                  <div className="flex items-center gap-3 text-left">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      {file.type === "application/pdf" ? <FileText className="h-6 w-6" /> : <ImageIcon className="h-6 w-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type}
                      </p>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="h-9 w-9 rounded-lg hover:bg-destructive/10 hover:text-destructive flex items-center justify-center"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                      <UploadCloud className="h-7 w-7" />
                    </div>
                    <p className="mt-4 font-semibold">Drag & drop your file here</p>
                    <p className="text-sm text-muted-foreground mt-1">PNG, JPG, or PDF · up to {MAX_MB}MB</p>
                    <input
                      ref={inputRef}
                      type="file"
                      accept={ACCEPT}
                      className="hidden"
                      onChange={onChange}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={() => inputRef.current?.click()}
                    >
                      Choose File
                    </Button>
                  </>
                )}
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full gradient-primary text-primary-foreground shadow-glow h-11"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading…</>
                ) : (
                  "Upload File"
                )}
              </Button>
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
